import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef, useState } from "react";
/**
 * The popup header's second row (under the title line, above the rule). A view inside the popup portals
 * into it, e.g. the booking form's Back button. Null outside a popup.
 */
const PopupHeaderSlot = createContext(null);
export function usePopupHeaderSlot() {
    return useContext(PopupHeaderSlot);
}
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
/** True when the current location asks for this popup (its route or its query flag). */
export function usePopupOpen(chrome, router) {
    return popupOpenAt(chrome, router.useLocation());
}
/** Same test for a location already in hand; a view with no popup chrome is never in a popup. */
export function popupOpenAt(chrome, location) {
    if (!chrome)
        return false;
    const params = new URLSearchParams(location.search);
    return location.pathname === chrome.openPath || params.get(chrome.openQuery.key) === chrome.openQuery.value;
}
/**
 * Focus trap for an open popup: moves focus inside, keeps Tab/Shift+Tab inside, and hands focus
 * back to whatever opened it on close. Escape calls `onClose`.
 */
function usePopupFocus(dialogRef, onClose) {
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    useEffect(() => {
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const dialog = dialogRef.current;
        const focusables = () => (dialog ? Array.from(dialog.querySelectorAll(FOCUSABLE)) : []);
        (focusables()[0] ?? dialog)?.focus();
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                closeRef.current();
                return;
            }
            if (event.key !== "Tab" || !dialog)
                return;
            const items = focusables();
            if (items.length === 0) {
                event.preventDefault();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            const active = document.activeElement;
            if (event.shiftKey && (active === first || !dialog.contains(active))) {
                event.preventDefault();
                last.focus();
            }
            else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
                event.preventDefault();
                first.focus();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            if (opener && opener.isConnected)
                opener.focus();
        };
    }, [dialogRef]);
}
/** The dialog itself; mounted only while the popup is open so the focus effects follow its life. */
function PopupFrame({ chrome, prefix, dataAttr, onClose, dialogRef, children, }) {
    usePopupFocus(dialogRef, onClose);
    const [headerSlot, setHeaderSlot] = useState(null);
    const data = { [dataAttr]: "open" };
    return (_jsx("div", { className: `${prefix}-backdrop`, role: "dialog", "aria-modal": "true", "aria-label": chrome.ariaLabel, ...data, onClick: onClose, children: _jsxs("div", { ref: dialogRef, className: `${prefix}-dialog`, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: `${prefix}-topbar`, children: [_jsx("span", { className: `${prefix}-kicker`, children: chrome.kicker }), _jsx("button", { type: "button", className: `${prefix}-close`, "aria-label": chrome.closeLabel, onClick: onClose, children: chrome.closeGlyph }), _jsx("div", { ref: setHeaderSlot, className: `${prefix}-subbar`, "data-popup-subbar": true })] }), _jsx(PopupHeaderSlot.Provider, { value: headerSlot, children: _jsx("div", { className: `${prefix}-body`, children: children }) })] }) }));
}
/**
 * Shared popup shell (backdrop, dialog, topbar, body) for the booking and contact forms.
 * Markup is block/grid only (see `noFlexInPopups.test.ts`); the CSS is `@lumora/ui/book.css`.
 * Escape, the backdrop and the close button go to `chrome.closePath`.
 */
export function SitePopup({ chrome, router, prefix, dataAttr, dialogRef, children, }) {
    const isOpen = usePopupOpen(chrome, router);
    const navigate = router.useNavigate();
    if (!isOpen)
        return null;
    return (_jsx(PopupFrame, { chrome: chrome, prefix: prefix, dataAttr: dataAttr, dialogRef: dialogRef, onClose: () => navigate(chrome.closePath), children: children }));
}
//# sourceMappingURL=SitePopup.js.map